
import { defineComponent } from "vue";

// Sanitize text input to prevent XSS attacks
function sanitizeTextInput(input: string): string {
  if (!input) return input;
  return input
    .replace(/[<>'"&]/g, '') // Remove HTML special characters
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/style\s*=\s*["'][^"']*["']/gi, '') // Remove style attributes
    .replace(/src\s*=\s*["'][^"']*["']/gi, '') // Remove src attributes
    .replace(/data:\s*["'][^"']*["']/gi, '') // Remove data: URIs
    .replace(/<!--[\s\S]*?-->/g, '') // Remove HTML comments
    .replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi, '') // Basic SQL injection prevention
    .trim()
    .substring(0, 255); // Limit length to prevent buffer overflow
}

export default defineComponent({
  name: "FormInput",
  props: {
    label: { type: String, required: true },
    modelValue: { type: String, required: true },
    type: { type: String, default: "text" },
    placeholder: { type: String, default: "" },
    required: { type: Boolean, default: false },
    helpText: { type: String, default: "" },
    id: { type: String, required: true },
    maxLength: { type: Number, default: 255 },
    sanitize: { type: Boolean, default: true },
    validationPattern: { type: RegExp, default: null },
    customValidator: { type: Function, default: null }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      localValue: this.modelValue
    }
  },
  computed: {
    helpId() {
      return this.helpText ? `${this.id}-help` : "";
    },
    errorId() {
      return `${this.id}-error`;
    },
    ariaDescribedBy() {
      const ids = [];
      if (this.helpText) ids.push(this.helpId);
      if (this.errorMessage) ids.push(this.errorId);
      return ids.join(' ') || undefined;
    },
    showError() {
      return this.errorMessage && this.localValue;
    },
    errorMessage() {
      if (!this.localValue && this.required) return "";
      if (!this.localValue) return "";

      if (this.validationPattern && !this.validationPattern.test(this.localValue)) {
        return this.getValidationErrorMessage();
      }

      if (this.customValidator && !this.customValidator(this.localValue)) {
        return this.getCustomErrorMessage();
      }

      return "";
    }
  },
  methods: {
    handleInput(event: Event) {
      const target = event.target as HTMLInputElement;
      let value = target.value;

      if (this.sanitize) {
        value = sanitizeTextInput(value);
        if (value.length > this.maxLength) {
          value = value.substring(0, this.maxLength);
        }
        target.value = value;
      }

      this.localValue = value;
      this.$emit('update:modelValue', value);
    },
    getValidationErrorMessage() {
      if (this.type === 'email') {
        return "Por favor, introduza um email válido";
      }
      return "Formato inválido";
    },
    getCustomErrorMessage() {
      return "Valor inválido";
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.localValue = newVal;
      },
      immediate: true
    }
  }
});
